import { θProviderNetworkCompany } from '@sondermind/utilities/models-client-facing';

import { BenefitVerification } from './benefit-verification';
import { ICardPicture } from './card-picture.interface';
import { IClientInsuranceResponse } from './client-insurance-response.interface';

/**
 * @deprecated Use `IPatientInsurance` from `@sondermind/data-access/client-insurance` instead. This
 * snake_case class predates the camelCase interface and models the same `PatientInsuranceOutput`
 * serializer. It survives only for `create()`/`update()`, whose `with_create_update_bv_info` view
 * returns a `recent_benefit_verification` payload `IPatientInsurance` does not model.
 */
export class ClientInsurance {
  id: number;
  created_at: Date;
  updated_at: Date;
  deleted: boolean;

  group_number: string;
  policy_number: string;
  group_plan_name: string;
  eap_company_name: string;
  eap: boolean;

  first_name: string;
  last_name: string;
  middle_name: string;
  date_of_birth: string;
  gender: string;
  gender_id: number;
  phone_number: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;

  subscriber_relationship: string;
  relationship_to_insured_id: number | null;

  front_card_picture: ICardPicture;
  back_card_picture: ICardPicture;
  payer_name: string;
  payer_address_line1: string;
  payer_address_line2: string;
  payer_city: string;
  payer_state: string;
  payer_zip: string;
  payer_phone: string;

  last_verified_date: string;
  last_verification: { aasm_state: string; verification_date: string; };
  requires_card_verification: boolean;
  card_verified: boolean;
  auth_notes: string;
  auth_number: string;
  client_name: string;
  persona_client_id: number;
  patient_id: number;
  dependent: boolean;
  patient_relationship_type_id: number;
  providers_can_add_to_clients: boolean;

  provider_network_company_id: number;
  provider_network_company: θProviderNetworkCompany;

  recent_benefit_verification: BenefitVerification;

  is_usage_based: boolean;
  is_pro_bono: boolean;
  is_redemption: boolean;

  provider_name: string;

  max_redemptions: number | undefined;
  used_redemptions: number | undefined;

  ranking: number | null;

  constructor(json?: IClientInsuranceResponse) {
    if (json) {
      this.id = json.id;
      this.created_at = json.created_at && new Date(json.created_at);
      this.updated_at = json.updated_at && new Date(json.updated_at);
      this.group_number = json.group_number;
      this.policy_number = json.policy_number;
      this.group_plan_name = json.group_plan_name;
      this.eap_company_name = json.eap_company_name;
      this.eap = !!json.eap;
      this.first_name = json.first_name;
      this.last_name = json.last_name;
      this.middle_name = json.middle_name;
      this.date_of_birth = json.date_of_birth;
      this.gender = json.gender;
      this.gender_id = json.gender_id;
      this.phone_number = json.phone_number;
      this.address_line1 = json.address_line1;
      this.address_line2 = json.address_line2;
      this.city = json.city;
      this.deleted = json.deleted;
      this.state = json.state;
      this.postal_code = json.postal_code;
      this.subscriber_relationship = json.subscriber_relationship;
      this.relationship_to_insured_id = json.relationship_to_insured_id ?? null;
      this.front_card_picture = json.front_card_picture || json.front_patient_card;
      this.back_card_picture = json.back_card_picture || json.back_patient_card;
      this.payer_name = json.payer_name;
      this.payer_address_line1 = json.payer_address_line1;
      this.payer_address_line2 = json.payer_address_line2;
      this.payer_city = json.payer_city;
      this.payer_state = json.payer_state;
      this.payer_zip = json.payer_zip;
      this.payer_phone = json.payer_phone;
      this.last_verified_date = json.last_verified_date;
      this.last_verification = json.last_verification;
      this.requires_card_verification = json.requires_card_verification;
      this.card_verified = json.card_verified;
      this.auth_notes = json.auth_notes;
      this.auth_number = json.auth_number;
      this.client_name = json.client_name;
      this.persona_client_id = json.persona_client_id;
      this.patient_id = json.patiend_id; // TODO: check 'patiend' spelling in backend
      this.provider_network_company_id = json.provider_network_company ? json.provider_network_company.id : json.provider_network_company_id;
      this.provider_network_company = json.provider_network_company && θProviderNetworkCompany.fromJson(json.provider_network_company);
      this.recent_benefit_verification = json.recent_benefit_verification && BenefitVerification.fromJson(json.recent_benefit_verification);
      this.dependent = json.dependent;
      this.patient_relationship_type_id = json.patient_relationship_type_id;
      this.providers_can_add_to_clients = json.provider_network_company?.providers_can_add_to_clients;
      this.is_usage_based = json.is_usage_based;
      this.is_pro_bono = json.is_pro_bono;
      this.is_redemption = json.is_redemption;
      this.max_redemptions = json.max_redemptions;
      this.used_redemptions = json.used_redemptions;
      this.provider_name = json.provider_name;
      this.ranking = json.ranking;
    }
  }

  get remaining_redemptions(): number | undefined {
    if (this.is_usage_based) {
      return this.max_redemptions - this.used_redemptions;
    }

    // undefined, because this isn't a redemption card
    // 0 remaining redemptions !== not a redemption network
    return undefined;
  }

  get canEdit(): boolean {
    return !this.deleted && this.providers_can_add_to_clients;
  }

  /**
   * If `ranking` is undefined or null, it means it is going through manual review.
   * Primary is always `1`, anything else is Secondary.
   */
  get insuranceRank(): string {
    if (this.ranking == null) {
      return 'In Review';
    } if (this.ranking === 1) {
      return 'Primary Insurance';
    }
    return 'Secondary Insurance';
  }
}
